import { isPermissionGranted } from "./navPermissions";
import { PagesPermissions } from "../types/Authorization";
import { readDetails } from "./companyDetails";
import type { AttachAction } from "./askAttachmentPlan";
import { arabicCompanyToEnglish, hasArabic, westernDigits } from "./arabicText";

/**
 * "Create new contact Sara Khoury +971 50 123 4567", "add a new client Gulf
 * Shield with contact Layla Haddad 050 555 0101", "new lead Najd Armoured":
 * a CRM record asked for in plain words, read by CODE on this computer — the
 * new names never reach the AI (D18), no credit is used. Ask answers with
 * what it read and one button that opens the form filled in; the person
 * checks and saves.
 */

export type CrmKind = "contact" | "client" | "provider" | "company" | "lead";

export interface CreateCommand {
    kind: CrmKind;
    /** What the person gave, one detail per line (name, phones, emails…), for the form's reader. */
    details: string;
    /** The record's name (an organisation's), when given. */
    name: string | null;
    /** "… at Gulf Shield": the organisation a new contact works for. */
    organization: string | null;
    /** "… with contact Layla Haddad 050…": a contact to open next, linked to the new record. */
    contact: string | null;
}

const KINDS: Array<[RegExp, CrmKind]> = [
    [/^(contact|person)$/i, "contact"],
    [/^(client|customer)$/i, "client"],
    [/^(provider|supplier|vendor)$/i, "provider"],
    [/^company$/i, "company"],
    [/^lead$/i, "lead"],
];
const KIND_WORDS = "contact|person|client|customer|provider|supplier|vendor|company|lead";
/**
 * The same request in Arabic (2026-10-15): "أضف جهة اتصال …", "أنشئ عميل محتمل …", "سجل مزود جديد اسمه …".
 * Groups: contact, lead, client, provider, company, then the rest.
 */
const ARABIC_COMMAND = /^\s*(?:من فضلك\s+|لو سمحت\s+|ممكن\s+|رجاء\s+)?(?:أضف|اضف|أضيف|اضيف|ضيف|أنشئ|انشئ|اعمل|سجل|سجّل|افتح)\s+(?:لي\s+)?(?:(جهة\s+(?:ال)?اتصال|شخص)|(عميل\s+محتمل|عميلا\s+محتملا|ليد)|(عميل|زبون)|(مزود(?:\s+خدمة)?|مورد)|(شركة))\s*(?:جديد|جديدة|جديده)?\s*[:،,-]?\s*(?:اسمه|اسمها|باسم|اسم)?\s*([\s\S]*)$/;
const ARABIC_KINDS: CrmKind[] = ["contact", "lead", "client", "provider", "company"];

const COMMAND = new RegExp(`^\\s*(?:please\\s+|can\\s+you\\s+|could\\s+you\\s+)?(?:(?:create|add|make|register|set\\s+up|open|start)\\s+(?:a\\s+|an\\s+)?(?:new\\s+)?|new\\s+)(${KIND_WORDS})\\b[\\s:,-]*(?:called\\s+|named\\s+|for\\s+)?([\\s\\S]*)$`, "i");

/** One detail per line, so the reader finds the name, each phone and each email. */
const asLines = (text: string): string =>
    text.replace(/\s*[,;]\s*/g, "\n")
        .replace(/\s+(?=(?:\+|00)\d)/g, "\n")
        // a local number ("050 555 0101")
        .replace(/\s+(?=0\d[\d\s().-]{6,}\d(?!\S))/g, "\n")
        .replace(/\s+(?=[\w.+-]+@[\w-]+\.)/g, "\n")
        .replace(/\s+(?=(?:phone|tel|mobile|mob|cell|whatsapp|email|e-mail)\b)/gi, "\n")
        .split("\n").map((line) => line.trim()).filter(Boolean).join("\n");

/**
 * The record a person asks to create, or null when the words aren't such a request.
 * @param text - What was typed in Ask.
 */
export const createCommand = (typed: string): CreateCommand | null => {
    const text = westernDigits(typed);
    // a question is a question ("can you add a new contact?"), not the request itself
    if (/[?؟]\s*$/.test(String(text ?? ""))) return null;
    const arabic = ARABIC_COMMAND.exec(text);
    if (arabic) return arabicCommand(arabic);
    const match = COMMAND.exec(String(text ?? ""));
    if (!match) return null;
    const kind = KINDS.find(([pattern]) => pattern.test(match[1]))?.[1];
    if (!kind) return null;
    let rest = match[2].trim().replace(/[.!]+$/, "");
    // a bare "new client …" must name one (a capitalised name, a phone, an email): "new client meeting tomorrow" isn't
    const hasVerb = /^\s*(?:please\s+|can\s+you\s+|could\s+you\s+)?(?:create|add|make|register|set\s+up|open|start)\b/i.test(String(text ?? ""));
    if (!hasVerb && !/^\s*["“']?(?:[\p{Lu}\p{Lo}]|\+|00\d)|[\w.+-]+@[\w-]+\./u.test(rest)) return null;
    let contact: string | null = null;
    const withContact = /\s+(?:with|and)\s+(?:a\s+|the\s+)?(?:contact|person)\s+([\s\S]+)$/i.exec(rest);
    if (withContact && kind !== "contact") {
        contact = asLines(withContact[1].trim());
        rest = rest.slice(0, withContact.index).trim();
    }
    let organization: string | null = null;
    if (kind === "contact") {
        const at = /\s+(?:at|from|of|works\s+(?:at|for))\s+([^,;\n+@]+?)(?=\s*(?:[,;\n]|(?:\+|00)\d|[\w.+-]+@|\b(?:phone|tel|mobile|email)\b|$))/i.exec(rest);
        if (at) {
            organization = at[1].trim();
            rest = `${rest.slice(0, at.index)}${rest.slice(at.index + at[0].length)}`.trim();
        }
    }
    const details = asLines(rest);
    const first = details.split("\n")[0] ?? "";
    const name = first && !/\d|@/.test(first) ? first : null;
    return { kind, details, name, organization, contact };
};

/**
 * An Arabic create request, read the same way: the person's name stays as
 * written for the form's reader (it spells it in English and keeps the Arabic
 * in the notes); an organisation's name is put in English here.
 */
const arabicCommand = (match: RegExpExecArray): CreateCommand | null => {
    const at = ARABIC_KINDS.findIndex((_, index) => !!match[index + 1]);
    const kind = ARABIC_KINDS[at];
    if (!kind) return null;
    let rest = match[6].trim().replace(/[.!]+$/, "");
    let contact: string | null = null;
    const withContact = /\s+(?:مع|و)\s*(?:جهة\s+(?:ال)?اتصال|المسؤول|الشخص)\s+([\s\S]+)$/.exec(rest);
    if (withContact && kind !== "contact") {
        contact = asLines(withContact[1].trim());
        rest = rest.slice(0, withContact.index).trim();
    }
    let organization: string | null = null;
    if (kind === "contact") {
        const from = /\s+(?:من|في|لدى|عند)\s+((?:شركة|مؤسسة|مجموعة)?\s*[^\n،,+@\d]+?)(?=\s*(?:[،,\n]|(?:\+|00)\d|\d|[\w.+-]+@|$))/.exec(rest);
        if (from) {
            organization = arabicCompanyToEnglish(from[1].trim()).english || from[1].trim();
            rest = `${rest.slice(0, from.index)}${rest.slice(from.index + from[0].length)}`.trim();
        }
    }
    const details = asLines(rest);
    const first = details.split("\n")[0] ?? "";
    const raw = first && !/\d|@/.test(first) ? first : null;
    const name = raw && kind !== "contact" && kind !== "lead" && hasArabic(raw) ? arabicCompanyToEnglish(raw).english || raw : raw;
    return { kind, details, name, organization, contact };
};

const RIGHT: Record<CrmKind, string> = {
    contact: PagesPermissions.CONTACTS_CREATE,
    client: PagesPermissions.CLIENTS_CREATE,
    provider: PagesPermissions.PROVIDERS_CREATE,
    company: PagesPermissions.COMPANIES_CREATE,
    lead: PagesPermissions.LEADS_CREATE,
};

/**
 * Ask's reply to a create request, read here: what it read and the button
 * that opens the form filled in. Null without the right to create it (the
 * question then goes on as usual).
 * @param command - The request read.
 * @param pages - The person's page permissions.
 */
export const commandReply = (command: CreateCommand, pages: any[]): { message: string; actions: AttachAction[] } | null => {
    if (!isPermissionGranted(pages, RIGHT[command.kind])) return null;
    const read = readDetails(command.details);
    const name = command.kind === "contact" || command.kind === "lead" ? read.person ?? command.name : command.name ?? read.company;
    const reach = [...read.phones.map((phone) => phone.number), ...read.emails, read.website].filter(Boolean);
    const fromArabic = (command.kind === "contact" || command.kind === "lead" ? !!read.personArabic : hasArabic(command.details.split("\n")[0] ?? "")) ? " (from Arabic — check spelling)" : "";
    const facts = [name ? `${name}${fromArabic}` : null, ...reach, command.organization ? `at ${command.organization}` : null].filter(Boolean).join(" · ");
    const contactName = command.contact ? readDetails(command.contact).person ?? command.contact.split("\n")[0] : null;
    const label = command.kind === "contact" ? "new contact" : command.kind === "lead" ? "new lead" : `new ${command.kind}`;
    const go: AttachAction["go"] = command.kind === "contact"
        ? { type: "contact", text: command.details, organizationName: command.organization ?? undefined }
        : command.kind === "lead"
            ? { type: "lead", text: command.details }
            : {
                type: "record_new", kind: command.kind, text: command.details, name: command.name ?? undefined,
                then: command.contact && isPermissionGranted(pages, PagesPermissions.CONTACTS_CREATE) ? [{ type: "contact", text: command.contact }] : [],
            };
    const then = command.contact && command.kind !== "contact" && command.kind !== "lead" && isPermissionGranted(pages, PagesPermissions.CONTACTS_CREATE)
        ? ` Once it's saved, a new contact${contactName ? ` for ${contactName}` : ""} opens, linked to it.`
        : "";
    return {
        message: `${label.charAt(0).toUpperCase()}${label.slice(1)}${facts ? `: ${facts}` : ""} — read here, nothing sent. Open it to check, add the rest and save.${then}`,
        actions: [{
            label: `Fill with AI: ${label}${contactName && then ? " + contact" : ""}`, icon: "stars", go,
            title: `Open a new ${command.kind} filled in with what you typed — you check each value and save.`,
        }],
    };
};
